import React, { useRef, useState } from 'react';
import { Button } from "@/components/ui/button";
import { UserCircle, X, Loader2 } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export default function PersonPhotoCapture({ onPhotoCaptured, onOfflineCapture }) {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleCapture = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      // The R2 key, not a URL. Contacts store keys, and the image routes
      // need a bearer token, so a URL would be no use in an <img src> anyway.
      const { key } = await base44.integrations.Core.UploadFile({ file, kind: 'person' });
      onPhotoCaptured(key);
    } catch (err) {
      if (err.status === 0 && onOfflineCapture) {
        onOfflineCapture(file);
        toast.warning("You're offline. The photo is saved and will upload once you're back online.");
      } else {
        console.error('Photo upload error:', err);
        toast.error(err.message || 'Could not upload the photo.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onPhotoCaptured(null);
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={(e) => { handleCapture(e.target.files[0]); e.target.value = ''; }}
      />

      <AnimatePresence mode="wait">
        {preview ? (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative rounded-xl overflow-hidden border-2 border-accent/30"
          >
            <img
              src={preview}
              alt="Person"
              className="w-full h-40 object-cover cursor-zoom-in"
              onClick={() => !uploading && setLightboxOpen(true)}
            />
            {uploading && (
              <div className="absolute inset-0 bg-primary/60 backdrop-blur-sm flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin text-white" />
              </div>
            )}
            {!uploading && (
              <button
                onClick={handleRemove}
                className="absolute top-2 right-2 p-1.5 bg-background/80 backdrop-blur rounded-full hover:bg-background transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        ) : (
          <motion.div key="capture" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-muted-foreground/20 rounded-xl p-5 flex flex-col items-center gap-2 hover:border-accent/40 hover:bg-accent/5 transition-all group"
            >
              <div className="w-11 h-11 rounded-full bg-secondary flex items-center justify-center group-hover:bg-accent/10 transition-colors">
                <UserCircle className="w-6 h-6 text-muted-foreground group-hover:text-accent" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">Take Person's Photo</p>
                <p className="text-xs text-muted-foreground mt-0.5">Optional: selfie or portrait of the contact</p>
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
            onClick={() => setLightboxOpen(false)}
          >
            <button className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/30" onClick={() => setLightboxOpen(false)}>
              <X className="w-5 h-5 text-white" />
            </button>
            <motion.img
              src={preview}
              alt="Person full size"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-full max-h-full rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}